import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Draggable from 'react-draggable';

import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import ButtonBase from '@material-ui/core/ButtonBase';

import Menu from 'components/common/Menu';
import DeleteMenu from 'components/common/DeleteMenu';
import IconButton from 'components/common/IconButton';
import IconCross from 'components/icons/IconCross';
import IconMoveHor from 'components/icons/IconMoveHor';
import IconTextbox from 'components/icons/IconTextbox';
import IconMultiLine from 'components/icons/IconMultiLine';
import IconRadio from 'components/icons/IconRadio';
import IconCheckboxChecked from 'components/icons/IconCheckboxChecked';
import IconDropdown from 'components/icons/IconDropdown';
import IconCalendarEnd from 'components/icons/IconCalendarEnd';
import IconSignature from 'components/icons/IconSignature';
import IconList from 'components/icons/IconList';
import IconTriangleTop from 'components/icons/IconTriangleTop';
import IconTriangleBottom from 'components/icons/IconTriangleBottom';
import IconTriangleLeft from 'components/icons/IconTriangleLeft';
import IconTriangleRight from 'components/icons/IconTriangleRight';
import IconPen from 'components/icons/IconPen';
import IconImage from 'components/icons/IconImage';

import pxToRem from 'styles/pxToRem';

import TemplatesCreateEditFormFieldMenu from './TemplatesCreateEditFormFieldMenu';

const styles = theme => ({
  draggable: {
    display: 'flex',
    height: '100%',
    position: 'relative',
    padding: 8,
    boxSizing: 'border-box',
  },
  gridDragged: {
    zIndex: 1,
  },
  paper: {
    width: '100%',
    position: 'relative',
    padding: '30px 27px 18px 24px',
    border: `1px solid ${theme.palette.primary.A100}`,
    borderRadius: 4,
  },
  paperWithTransition: {
    transition: theme.transitions.create(['transform'], {
      duration: theme.transitions.duration.short,
    }),
  },
  header: {
    padding: '0 0 19px 0',
    margin: '0 0 27px 0',
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.primary[600],
    borderBottom: `1px solid ${theme.palette.primary.A100}`,
  },
  body: {
    padding: '0 30px 0 40px',
  },
  typographyTitle: {
    marginLeft: 16,
    marginBottom: -1,
    fontSize: pxToRem(12),
    fontWeight: 700,
    textTransform: 'uppercase',
    color: theme.palette.primary[600],
  },
  typographyQuestionPlaceholder: {
    marginBottom: 22,
  },
  typographyQuestionPlaceholderTextbox: {
    marginBottom: 10,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 4,
    },
  },
  typographyAnswerPlaceholder: {
    marginTop: 5,
    padding: '8px 16px',
    border: `1px solid ${theme.palette.primary.A400}`,
    borderRadius: 4,
  },
  typographyAnswerPlaceholderMultiline: {
    paddingBottom: 46,
  },
  typographySignatureLabelPlaceholder: {
    fontWeight: 700,
    marginTop: 16,
    marginBottom: 8,
    color: theme.palette.grey[600],
  },
  typographyDateStampPlaceholder: {
    marginTop: 16,
  },
  typographyPatientInformationPlaceholder: {
    marginLeft: -64,
    marginRight: -30,
    padding: '16px 112px 0 26px',
    borderTop: `1px solid ${theme.palette.primary.A100}`,
  },
  buttonMove: {
    position: 'absolute',
    left: '50%',
    top: -10,
    transform: 'translateX(-50%)',
  },
  buttonEdit: {
    position: 'absolute',
    left: 12,
    top: 80,
    color: theme.palette.primary.A400,
  },
  buttonRemove: {
    position: 'absolute',
    right: 10,
    top: 15,
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    position: 'absolute',
    left: 12,
    top: 15,
    color: theme.palette.primary[600],
  },
  iconTextbox: {
    width: 18,
    height: 16,
  },
  iconMultiLine: {
    width: 18,
    height: 16,
  },
  iconRadio: {
    height: 20,
  },
  iconCheckboxChecked: {
    height: 16,
    width: 16,
  },
  iconDropdown: {
    height: 16,
  },
  iconCalendarEnd: {
    height: 20,
  },
  iconSignature: {
    height: 18,
  },
  iconList: {
    width: 18,
    height: 10,
  },
  iconCross: {
    width: 11,
    height: 11,
  },
  iconMoveHor: {
    width: 16,
    height: 10,
  },
  iconPen: {
    width: 16,
    height: 16,
  },
  radioPlaceholder: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 5,
    '& + $radioPlaceholder': {
      marginTop: 8,
    },
  },
  iconRadioPlaceholder: {
    width: 16,
    height: 16,
    marginRight: 16,
    borderRadius: '50%',
    flexShrink: 0,
    border: `2px solid ${theme.palette.primary.A400}`,
  },
  checkboxPlaceholder: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 5,
    '& + $checkboxPlaceholder': {
      marginTop: 8,
    },
  },
  iconCheckboxPlaceholder: {
    width: 14,
    height: 14,
    marginRight: 16,
    flexShrink: 0,
    borderRadius: 2,
    border: `2px solid ${theme.palette.primary.A400}`,
  },
  dropdownPlaceholder: {
    position: 'relative',
    width: '50%',
    marginTop: 5,
    padding: '9px 24px 9px 16px',
    borderRadius: 4,
    border: `1px solid ${theme.palette.primary.A400}`,
    '& + $dropdownPlaceholder': {
      marginTop: 8,
    },
  },
  iconDropdownPlaceholder: {
    position: 'absolute',
    top: '50%',
    right: 13,
    pointerEvents: 'none',
    transform: 'translateY(-50%)',
  },
  iconTriangleTopBottom: {
    display: 'block',
    width: 10,
    height: 5,
    margin: '5px 0',
    color: theme.palette.grey[600],
  },
  iconTriangleLeftRight: {
    width: 5,
    height: 10,
    color: theme.palette.common.white,
  },
  datePlaceholder: {
    position: 'relative',
    width: '50%',
    marginTop: 5,
    padding: '9px 24px 9px 16px',
    borderRadius: 4,
    border: `1px solid ${theme.palette.primary.A400}`,
    '& $iconCalendarEnd': {
      position: 'absolute',
      top: '50%',
      right: 10,
      pointerEvents: 'none',
      transform: 'translateY(-50%)',
      color: theme.palette.grey[600],
    },
  },
  buttonSwitchWidth: {
    position: 'absolute',
    width: 10,
    top: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.palette.primary.A100,
    height: 'auto',
    borderTopLeftRadius: 0,
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
    borderBottomLeftRadius: 0,
    transition: theme.transitions.create(['background-color'], {
      duration: theme.transitions.duration.short,
    }),
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.A400,
    },
  },
  iconImage: {

  },
});

class TemplatesCreateEditFormField extends Component {
  constructor(props) {
    super(props);

    this.state = {
      dragged: false,
      position: null,
      bounds: null,
      anchorEl: null,
      deleteMenuOpen: false,
      editMenuOpen: false,
      axis: props.input.value['Grid Width'] === 6 ? 'both' : 'y',
      axisSet: false,
    };

    this.onMenuClose = this.onMenuClose.bind(this);
    this.onRemoveButtonClick = this.onRemoveButtonClick.bind(this);
    this.onRemoveCancel = this.onRemoveCancel.bind(this);
    this.onRemoveSubmit = this.onRemoveSubmit.bind(this);
    this.onEditButtonClick = this.onEditButtonClick.bind(this);
    this.onDraggableDataChange = this.onDraggableDataChange.bind(this);
    this.onStartDrag = this.onStartDrag.bind(this);
    this.onDrag = this.onDrag.bind(this);
    this.onStopDrag = this.onStopDrag.bind(this);
    this.onSwitchWidthButtonClick = this.onSwitchWidthButtonClick.bind(this);
  }

  componentDidMount() {
    this.draggableDataChangeTimeout = setTimeout(() => {
      this.onDraggableDataChange();
    }, 100);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.input.value['Grid Width'] !== nextProps.input.value['Grid Width']) {
      if (nextProps.input.value['Grid Width'] !== 12) {
        this.setState({ axis: 'both' });
      } else {
        this.setState({ axis: 'y' });
      }
    }
  }

  componentDidUpdate(prevProps) {
    const { input, timestamp } = this.props;
    const choicesChanged = input.value.Choices
      && prevProps.input.value.Choices
      && input.value.Choices.length !== prevProps.input.value.Choices.length;
    if (timestamp !== prevProps.timestamp
      || input.value['Question Type'] !== prevProps.input.value['Question Type']
      || input.value.Question !== prevProps.input.value.Question
      || input.value['Grid Width'] !== prevProps.input.value['Grid Width']
      || choicesChanged) {
      this.draggableDataChangeTimeout = setTimeout(() => {
        this.onDraggableDataChange();
      }, 100);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.draggableDataChangeTimeout);
  }

  onDraggableDataChange() {
    const { boundsSelector, input, onDraggableDataChange } = this.props;
    if (boundsSelector) {
      const draggableRefDOMRect = this.draggableRef.getBoundingClientRect();
      const boundsDOMRect = document.querySelector(boundsSelector).getBoundingClientRect();
      const left = Math.round(boundsDOMRect.left - draggableRefDOMRect.left);
      const top = Math.round(boundsDOMRect.top - draggableRefDOMRect.top);
      const right = Math.round((boundsDOMRect.width - draggableRefDOMRect.width) + left);
      const bottom = Math.round((boundsDOMRect.height - draggableRefDOMRect.height) + top);
      const data = {
        bounds: { left, top, right, bottom },
        width: draggableRefDOMRect.width,
        height: draggableRefDOMRect.height,
      };
      const draggableData = {};
      draggableData[input.name] = data;
      this.setState(data);
      onDraggableDataChange(draggableData);
    }
  }

  onStartDrag(event, data) {
    const { onStartDrag, index } = this.props;
    const { bounds, width, height } = this.state;
    this.onDraggableDataChange();
    this.setState({ dragged: true });
    if (onStartDrag) {
      onStartDrag({
        x: data.x - bounds.left,
        y: data.y - bounds.top,
        width,
        height,
        defaultX: -bounds.left,
        defaultY: -bounds.top,
        index,
      });
    }
  }

  onDrag(event, data) {
    const { onDrag, index, input: { value } } = this.props;
    const { bounds, width, height, axisSet, axis } = this.state;
    const threshold = 8;
    if (value['Grid Width'] !== 12
      && !axisSet
      && (Math.abs(data.x) >= threshold || Math.abs(data.y) >= threshold)) {
      this.setState({
        axis: Math.abs(data.x) > Math.abs(data.y) ? 'x' : 'y',
        axisSet: true,
      });
    }
    if (onDrag) {
      onDrag({
        x: axis === 'x' ? data.x - bounds.left : -bounds.left,
        y: axis === 'y' ? data.y - bounds.top : -bounds.top,
        width,
        height,
        defaultX: -bounds.left,
        defaultY: -bounds.top,
        index,
      });
    }
  }

  onStopDrag(event, data) {
    const { onStopDrag, index, input: { value } } = this.props;
    const { bounds, width, height } = this.state;
    this.setState({
      dragged: false,
      position: {
        x: 0,
        y: 0,
      },
      axis: value['Grid Width'] === 12 ? 'y' : 'both',
      axisSet: false,
    });
    if (onStopDrag) {
      onStopDrag({
        x: data.x - bounds.left,
        y: data.y - bounds.top,
        width,
        height,
        defaultX: -bounds.left,
        defaultY: -bounds.top,
        index,
      });
    }
  }

  onMenuClose() {
    this.setState({
      anchorEl: null,
      deleteMenuOpen: false,
      editMenuOpen: false,
    });
  }

  onRemoveButtonClick(event) {
    this.setState({
      anchorEl: event.currentTarget,
      deleteMenuOpen: true,
    });
  }

  onRemoveCancel() {
    this.setState({
      anchorEl: null,
      deleteMenuOpen: false,
    });
  }

  onRemoveSubmit() {
    this.props.onRemove();
    this.setState({
      anchorEl: null,
      deleteMenuOpen: false,
    });
  }

  onEditButtonClick(event) {
    this.setState({
      anchorEl: event.currentTarget,
      editMenuOpen: true,
    });
  }

  onSwitchWidthButtonClick() {
    const { onGridWidthSwitch, input: { value, onChange } } = this.props;
    if (value['Grid Width'] === 6) {
      onChange({
        ...value,
        ...{ 'Grid Width': 12 },
      });
    } else {
      onChange({
        ...value,
        ...{ 'Grid Width': 6 },
      });
    }
    if (onGridWidthSwitch) {
      onGridWidthSwitch();
    }
  }

  render() {
    const {
      input: {
        value,
        name,
        onChange,
      },
      offsetY,
      offsetX,
      withTransition,
      classes,
    } = this.props;
    let icon;
    switch (value['Question Type']) {
      case 'Textbox':
        icon = (<IconTextbox className={classes.iconTextbox} />);
        break;
      case 'MultiLine':
        icon = (<IconMultiLine className={classes.iconMultiLine} />);
        break;
      case 'Radio':
        icon = (<IconRadio className={classes.iconRadio} />);
        break;
      case 'Checkbox':
        icon = (<IconCheckboxChecked className={classes.iconCheckboxChecked} />);
        break;
      case 'DropDown':
        icon = (<IconDropdown className={classes.iconDropdown} />);
        break;
      case 'Date':
        icon = (<IconCalendarEnd className={classes.iconCalendarEnd} />);
        break;
      case 'Signature':
        icon = (<IconSignature className={classes.iconSignature} />);
        break;
      case 'Company Logo':
        icon = (<IconImage className={classes.iconImage} />);
        break;
      case 'Patient General':
      case 'Patient Insurance':
      case 'Patient Vitals':
      case 'Patient Prescribers':
      case 'Patient Diagnosis':
      case 'HCPCS Codes':
      case 'Company Information':
        icon = (<IconList className={classes.iconList} />);
        break;
      default:
        icon = '';
    }
    const {
      anchorEl,
      deleteMenuOpen,
      editMenuOpen,
      dragged,
      bounds,
      position,
      axis,
    } = this.state;
    return (
      <Draggable
        axis={axis}
        handle={`.${classes.buttonMove}`}
        {...bounds ? { bounds } : {}}
        {...position ? { position } : {}}
        onStart={this.onStartDrag}
        onDrag={this.onDrag}
        onStop={this.onStopDrag}
      >
        <Grid
          item
          xs={value['Grid Width'] || 12}
          className={classNames({
            [classes.gridDragged]: dragged,
          })}
        >
          <div
            className={classes.draggable}
            ref={(draggableRef) => {
              this.draggableRef = draggableRef;
            }}
          >
            <Paper
              className={classNames(classes.paper, {
                [classes.paperWithTransition]: withTransition,
              })}
              style={{ transform: `translateY(${offsetY}px) translateX(${offsetX}px)` }}
            >
              <IconButton
                className={classes.buttonMove}
                tooltipContent="Drag it to Change Position"
              >
                <IconMoveHor className={classes.iconMoveHor} />
              </IconButton>
              {['Textbox', 'MultiLine', 'Radio', 'Checkbox', 'DropDown', 'Date', 'Signature']
                .indexOf(value['Question Type']) !== -1 ? (
                  <Fragment>
                    <div className={classes.header}>
                      {icon}
                      <Typography className={classes.typographyTitle}>
                        {value['Question Type']}
                      </Typography>
                    </div>
                    <IconButton
                      className={classes.buttonEdit}
                      aria-label="Edit"
                      aria-owns={editMenuOpen ? `${name}-edit-menu` : null}
                      aria-haspopup="true"
                      onClick={this.onEditButtonClick}
                      tooltipContent="Edit"
                    >
                      <IconPen className={classes.iconPen} />
                    </IconButton>
                    <Menu
                      id={`${name}-edit-menu`}
                      anchorEl={anchorEl}
                      open={editMenuOpen}
                      onClose={this.onMenuClose}
                      placement="bottom-left"
                      padding="none"
                      list={false}
                      maxHeight={['Radio', 'Checkbox', 'DropDown']
                        .indexOf(value['Question Type']) !== -1 ? 340 : 185}
                    >
                      <TemplatesCreateEditFormFieldMenu
                        form={`${name}-edit`}
                        variant={value['Question Type']}
                        onCancel={this.onMenuClose}
                        initialValues={value}
                        onSubmitSuccess={(values) => {
                          onChange({
                            ...value,
                            ...values,
                          });
                          this.onMenuClose();
                        }}
                      />
                    </Menu>
                  </Fragment>
                ) : (
                  <div className={classes.iconContainer}>
                    {icon}
                  </div>
                )}
              <div className={classes.body}>
                {(['Textbox', 'MultiLine', 'Radio', 'Checkbox', 'DropDown', 'Date']
                  .indexOf(value['Question Type']) !== -1 && value.Text) &&
                  <Typography
                    className={classNames(classes.typographyQuestionPlaceholder, {
                      [classes.typographyQuestionPlaceholderTextbox]: value['Question Type'] === 'Textbox',
                    })}
                    color="secondary"
                  >
                    {value.Text.split('\n').map((item, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Fragment key={index}>
                        {item}
                        <br />
                      </Fragment>
                    ))}
                  </Typography>
                }
                {value['Question Type'] === 'Patient General' &&
                  <Typography
                    className={classes.typographyQuestionPlaceholder}
                    color="secondary"
                  >
                    Patient Information
                  </Typography>
                }
                {value['Question Type'] === 'Patient Insurance' &&
                  <Typography
                    className={classes.typographyQuestionPlaceholder}
                    color="secondary"
                  >
                    Patient Insurance
                  </Typography>
                }
                {value['Question Type'] === 'Patient Vitals' &&
                  <Typography
                    className={classes.typographyQuestionPlaceholder}
                    color="secondary"
                  >
                    Patient Vitals
                  </Typography>
                }
                {value['Question Type'] === 'Patient Prescribers' &&
                  <Typography
                    className={classes.typographyQuestionPlaceholder}
                    color="secondary"
                  >
                    Prescribers Information
                  </Typography>
                }
                {value['Question Type'] === 'Patient Diagnosis' &&
                  <Typography
                    className={classes.typographyQuestionPlaceholder}
                    color="secondary"
                  >
                    Diagnoses
                  </Typography>
                }
                {value['Question Type'] === 'HCPCS Codes' &&
                  <Typography
                    className={classes.typographyQuestionPlaceholder}
                    color="secondary"
                  >
                    HCPCS Codes
                  </Typography>
                }
                {value['Question Type'] === 'Company Information' &&
                  <Typography
                    className={classes.typographyQuestionPlaceholder}
                    color="secondary"
                  >
                    Company Information
                  </Typography>
                }
                {value['Question Type'] === 'Company Logo' &&
                  <Typography
                    className={classes.typographyQuestionPlaceholder}
                    color="secondary"
                  >
                    Company Logo
                  </Typography>
                }
                {value['Question Type'] === 'MultiLine' &&
                  <Typography
                    className={classNames(
                      classes.typographyAnswerPlaceholder,
                      classes.typographyAnswerPlaceholderMultiline,
                    )}
                    variant="caption"
                  >
                    Multi-line answer text
                  </Typography>
                }
                {value['Question Type'] === 'Radio'
                  && value.Choices.map((item, index) => (
                    <div
                      className={classes.radioPlaceholder}
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                    >
                      <div className={classes.iconRadioPlaceholder} />
                      <Typography
                        variant="caption"
                      >
                        {item}
                      </Typography>
                    </div>
                  ))}
                {value['Question Type'] === 'Checkbox'
                  && value.Choices.map((item, index) => (
                    <div
                      className={classes.checkboxPlaceholder}
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                    >
                      <div className={classes.iconCheckboxPlaceholder} />
                      <Typography
                        variant="caption"
                      >
                        {item}
                      </Typography>
                    </div>
                  ))}
                {value['Question Type'] === 'DropDown'
                  && value.Choices.map((item, index) => (
                    <div
                      className={classes.dropdownPlaceholder}
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                    >
                      <Typography
                        variant="caption"
                      >
                        {item}
                      </Typography>
                      <div className={classes.iconDropdownPlaceholder}>
                        <IconTriangleTop className={classes.iconTriangleTopBottom} />
                        <IconTriangleBottom className={classes.iconTriangleTopBottom} />
                      </div>
                    </div>
                  ))}
                {value['Question Type'] === 'Date' &&
                  <div className={classes.datePlaceholder}>
                    <Typography
                      variant="caption"
                    >
                      mm/dd/yyyy
                    </Typography>
                    <IconCalendarEnd className={classes.iconCalendarEnd} />
                  </div>
                }
                {value['Question Type'] === 'Signature' &&
                  <Fragment>
                    {value.Text &&
                      <Typography
                        className={classNames(
                          classes.typographyAnswerPlaceholder,
                          classes.typographyAnswerPlaceholderMultiline,
                        )}
                        variant="caption"
                      >
                        {value.Text}
                      </Typography>
                    }
                    <Typography
                      className={classes.typographySignatureLabelPlaceholder}
                      variant="caption"
                    >
                      Physician Signature
                    </Typography>
                    <Typography
                      className={classes.typographyAnswerPlaceholder}
                      variant="caption"
                    >
                      {'\u00A0'}
                    </Typography>
                    <Typography
                      className={classes.typographyDateStampPlaceholder}
                      align="right"
                      variant="caption"
                    >
                      Date Stamp for Signature
                    </Typography>
                  </Fragment>
                }
                {value['Question Type'] === 'Patient General' &&
                  <Typography
                    className={classes.typographyPatientInformationPlaceholder}
                  >
                    Collects First Name, Last Name, Date of Birth, Age, Gender
                  </Typography>
                }
                {value['Question Type'] === 'Patient Insurance' &&
                  <Typography
                    className={classes.typographyPatientInformationPlaceholder}
                  >
                    Collects Added Insurance Сompany Names and Insurance IDs
                  </Typography>
                }
                {value['Question Type'] === 'Patient Vitals' &&
                  <Typography
                    className={classes.typographyPatientInformationPlaceholder}
                  >
                    Collects Added Vitals: Weight, Height, BMI and Date of Creation
                  </Typography>
                }
                {value['Question Type'] === 'Patient Prescribers' &&
                  <Typography
                    className={classes.typographyPatientInformationPlaceholder}
                  >
                    Collects Added Prescribers First Name, Last Name, Email, Phone, NPI and LIC
                  </Typography>
                }
                {value['Question Type'] === 'Patient Diagnosis' &&
                  <Typography
                    className={classes.typographyPatientInformationPlaceholder}
                  >
                    Collects Added Diagnoses Code and Description
                  </Typography>
                }
                {value['Question Type'] === 'HCPCS Codes' &&
                  <Typography
                    className={classes.typographyPatientInformationPlaceholder}
                  >
                    Collects Added HCPC S Code, Quantity and Description
                  </Typography>
                }
                {value['Question Type'] === 'Company Information' &&
                  <Typography
                    className={classes.typographyPatientInformationPlaceholder}
                  >
                    Collects Company Name, Phone Number, Fax, Email and NPI
                  </Typography>
                }
                {value['Question Type'] === 'Company Logo' &&
                  <Typography
                    className={classes.typographyPatientInformationPlaceholder}
                  >
                    Displays Company Logo image
                  </Typography>
                }
              </div>
              <IconButton
                className={classes.buttonRemove}
                aria-label="Remove"
                aria-owns={deleteMenuOpen ? `${name}-template-item-remove-menu` : null}
                aria-haspopup="true"
                onClick={this.onRemoveButtonClick}
                tooltipContent="Delete"
              >
                <IconCross className={classes.iconCross} />
              </IconButton>
              <Menu
                id={`${name}-template-item-remove-menu`}
                anchorEl={anchorEl}
                open={deleteMenuOpen}
                onClose={this.onMenuClose}
                list={false}
              >
                <DeleteMenu
                  text="Are you sure you want to delete this question?"
                  onCancel={this.onRemoveCancel}
                  onSubmit={this.onRemoveSubmit}
                />
              </Menu>
              <ButtonBase
                className={classes.buttonSwitchWidth}
                onClick={this.onSwitchWidthButtonClick}
              >
                {value['Grid Width'] === 6 ? (
                  <IconTriangleRight className={classes.iconTriangleLeftRight} />
                ) : (
                  <IconTriangleLeft className={classes.iconTriangleLeftRight} />
                )}
              </ButtonBase>
            </Paper>
          </div>
        </Grid>
      </Draggable>
    );
  }
}

TemplatesCreateEditFormField.propTypes = {
  index: PropTypes.number.isRequired,
  input: PropTypes.shape({
    value: PropTypes.oneOfType([
      PropTypes.shape({
        'Question Type': PropTypes.string,
      }),
      PropTypes.string,
    ]),
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
  onStartDrag: PropTypes.func.isRequired,
  onDrag: PropTypes.func,
  onStopDrag: PropTypes.func,
  onGridWidthSwitch: PropTypes.func,
  boundsSelector: PropTypes.string,
  offsetY: PropTypes.number,
  offsetX: PropTypes.number,
  withTransition: PropTypes.bool,
  timestamp: PropTypes.number.isRequired,
  onDraggableDataChange: PropTypes.func.isRequired,
  classes: PropTypes.shape({
    draggable: PropTypes.string.isRequired,
    gridDragged: PropTypes.string.isRequired,
    paper: PropTypes.string.isRequired,
    paperWithTransition: PropTypes.string.isRequired,
    header: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    typographyTitle: PropTypes.string.isRequired,
    typographyQuestionPlaceholder: PropTypes.string.isRequired,
    typographyQuestionPlaceholderTextbox: PropTypes.string.isRequired,
    typographyAnswerPlaceholder: PropTypes.string.isRequired,
    typographyAnswerPlaceholderMultiline: PropTypes.string.isRequired,
    typographySignatureLabelPlaceholder: PropTypes.string.isRequired,
    typographyDateStampPlaceholder: PropTypes.string.isRequired,
    typographyPatientInformationPlaceholder: PropTypes.string.isRequired,
    buttonMove: PropTypes.string.isRequired,
    buttonEdit: PropTypes.string.isRequired,
    buttonRemove: PropTypes.string.isRequired,
    iconContainer: PropTypes.string.isRequired,
    iconTextbox: PropTypes.string.isRequired,
    iconMultiLine: PropTypes.string.isRequired,
    iconRadio: PropTypes.string.isRequired,
    iconCheckboxChecked: PropTypes.string.isRequired,
    iconDropdown: PropTypes.string.isRequired,
    iconCalendarEnd: PropTypes.string.isRequired,
    iconSignature: PropTypes.string.isRequired,
    iconList: PropTypes.string.isRequired,
    iconCross: PropTypes.string.isRequired,
    iconMoveHor: PropTypes.string.isRequired,
    iconPen: PropTypes.string.isRequired,
    radioPlaceholder: PropTypes.string.isRequired,
    iconRadioPlaceholder: PropTypes.string.isRequired,
    checkboxPlaceholder: PropTypes.string.isRequired,
    iconCheckboxPlaceholder: PropTypes.string.isRequired,
    dropdownPlaceholder: PropTypes.string.isRequired,
    iconDropdownPlaceholder: PropTypes.string.isRequired,
    iconTriangleTopBottom: PropTypes.string.isRequired,
    iconTriangleLeftRight: PropTypes.string.isRequired,
    datePlaceholder: PropTypes.string.isRequired,
    buttonSwitchWidth: PropTypes.string.isRequired,
  }).isRequired,
};

TemplatesCreateEditFormField.defaultProps = {
  boundsSelector: null,
  onStartDrag: null,
  onDrag: null,
  onStopDrag: null,
  onGridWidthSwitch: null,
  offsetY: 0,
  offsetX: 0,
  withTransition: false,
};

export default withStyles(styles)(TemplatesCreateEditFormField);



// WEBPACK FOOTER //
// ./src/components/settings/TemplatesCreateEditForm/TemplatesCreateEditFormField.js